import "@assets/css/bootstrap-grid.css";
import { Spinner } from "@components/ui/spinner";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import { useQueryClient } from "@tanstack/react-query";
import { SkeletonTwo } from "@ui/external/components/skeleton";
import { getInputField } from "@utils/function/index.function";
import { showToast, throwErrorAlert } from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { FormField } from "@utils/interface/form.interface";
import {
  StudentPlacementFormData,
  StudentPlacementSchema,
} from "@utils/interface/form.schema";
import { IndividualQuery } from "@utils/interface/index.interface";
import { StudentPlacementData } from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";
import React, { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router";

const StudentPlacementForm: React.FC = () => {
  const { searchParams, handleAction } = useHandleAction({});
  const { type } = useParams();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const modalItem = searchParams.get("modal-item");
  const modalMode = searchParams.get("modal");

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<StudentPlacementFormData>({
    mode: "onChange",
    resolver: zodResolver(StudentPlacementSchema),
  });

  console.log("🚀 ~ errors:", errors);

  const { data: fetchData, isPending: fetchPending } = useListData<
    IndividualQuery<StudentPlacementData>
  >({
    endpoint: endpoint["student-placement"],
    key: `${pathname}-${modalItem}`,
    enabled: modalItem ? true : false,
    id: modalItem || "",
    window: false,
  });

  /* set default value */
  const { setIsRetrive }: any = usePending();

  useEffect(() => {
    setIsRetrive(modalItem ? fetchPending : false);
    if (fetchData?.status === 200 && !fetchPending) {
      const firstResult = fetchData?.data;
      console.log(firstResult);
      if (firstResult) {
        if (modalMode == "add") {
          return reset({});
        } else {
          reset(() => ({
            current_placement_company:
              fetchData?.data?.current_placement_company,
            current_role: fetchData?.data?.current_role,
            student: [
              {
                label: fetchData.data?.student?.user?.full_name,
                value: fetchData?.data?.student?.id,
              },
            ],
          }));
        }
      }
    }
  }, [fetchData?.data, fetchPending]);

  const handleMutate = (
    data: StudentPlacementFormData & {
      student: number | any;
      id?: number | string;
    }
  ) => {
    data = { ...data, student: data["student"][0]["value"] };
    if (modalItem) {
      data = {
        ...data,
        id: modalItem,
      };
    }
    return {
      data,
      endpoint: endpoint["student-placement"],
      method: modalItem ? METHODS.PUT : METHODS.POST,
    };
  };
  const handleSuccess = async ({
    status,
  }: {
    data: StudentPlacementData;
    status: number;
  }) => {
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });

      showToast({
        description: `Stduent Placement ${
          status === 200 ? "Updated" : "Created"
        }`,
        type: "success",
        title: "Success",
      });

      handleAction({
        type: "modal-close",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };

  const { setIsFormPending } = useSelectionStore();
  const handleError = async (error: any) => {
    console.log(error, "error00");
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }
    setIsFormPending(false);
    const errorMessage = error.data.response.data;
    throwErrorAlert(errorMessage);
  };

  const { mutate, isPending } = usePostData();

  const onSubmit: SubmitHandler<StudentPlacementFormData> = (data) => {
    //Add Mutation Data
    mutate(handleMutate(data), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  const formSchema: FormField[] = [
    {
      name: "student",
      label: "Student",
      colSpan: 12,
      type: "async",
      endpoint: endpoint["studentselect"],
      format: { key: "full_name", value: "id", secondary_key: "email" },
      placeholder: "Select Student",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "current_placement_company",
      label: "Current Placement Company",
      colSpan: 12,
      type: "text",
      placeholder: "Enter Placement Company",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "current_role",
      label: "Current Role",
      colSpan: 12,
      type: "text",
      placeholder: "Enter Role",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      type: "submit",
      value: `${searchParams.get("modal") || ""} ${(type || pathname)?.replace(
        "-",
        " "
      )}`,
      className: "w-full h-[47px] mt-[32px] capitalize",
      colSpan: 12,
      variant: "default",
      endIcon: isPending ? <Spinner /> : null,
      disabled: isPending ? true : false,
      visable: true,
    },
  ];

  if (fetchPending && modalItem)
    return (
      <>
        <SkeletonTwo />
      </>
    );

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="row w-full">
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            )
        )}
      </form>
    </>
  );
};
export default StudentPlacementForm;
